import {Localized} from "@fluent/react";
import {
	Button,
	Divider,
	IconButton,
	Popover,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, {useState} from "react";
import {Link, Route} from "react-router-dom";

import Nav from "../components/nav/Nav";
import OrganisationSelector from "../components/nav/OrganisationSelector";
import AdminPanelIcon from "../icons/AdminPanelIcon";
import useCurrentLocale from "../i18n/useCurrentLocale";
import {useAppSelector} from "../store/hooks";
import UserRole from "../store/models/UserRole";
import useFetchOrganisation from "../store/organisation/useFetchOrganisation";
import useUserHasRoleInOrganisation from "../store/organisation/useUserHasRoleInOrganisation";
import {selectSortedUserOrganisations} from "../store/organisation/selectUserOrganisations";

function NavigationBar(props: {organisationName: string}) {
	const [userHasRole] = useUserHasRoleInOrganisation();

	const theme = useTheme();

	return (
		<Nav
			logo={`/api/organisations/${props.organisationName}/theme/logo`}
			quickLinks={
				userHasRole(UserRole.Administrator) && (
					<Tooltip
						title={
							<Localized id="nav-organisation-administration">
								Organisation administration
							</Localized>
						}
					>
						<IconButton
							size="small"
							component={Link}
							to="/administration"
							style={{
								color: theme.palette.primary.contrastText,
								padding: theme.spacing(0.5),
							}}
						>
							<AdminPanelIcon />
						</IconButton>
					</Tooltip>
				)
			}
			organisationName={props.organisationName}
		>
			<Route exact path={["/", "/enrolment-application"]}>
				<OrgSelector organisationName={props.organisationName} />
			</Route>
		</Nav>
	);
}

function OrgSelector(props: {organisationName: string}) {
	const locale = useCurrentLocale();
	const organisations = useAppSelector((state) =>
		selectSortedUserOrganisations(state, locale)
	);

	const [currentOrganisation] = useFetchOrganisation(props.organisationName);

	const [
		selectorAnchor,
		setSelectorAnchor,
	] = useState<HTMLButtonElement | null>(null);

	if (!currentOrganisation) {
		return <></>;
	}

	const userHasOtherOrganisations =
		organisations.length > 0 &&
		(organisations.length !== 1 ||
			organisations[0].name !== currentOrganisation.name);

	if (!userHasOtherOrganisations) {
		return (
			<Typography variant="button" style={{textTransform: "none"}}>
				{currentOrganisation.displayName}
			</Typography>
		);
	}

	function closeSelector() {
		setSelectorAnchor(null);
	}

	return (
		<>
			<Button
				color="inherit"
				disableRipple
				endIcon={<ExpandMoreIcon />}
				onClick={(event) => {
					setSelectorAnchor(event.currentTarget);
				}}
				sx={{textTransform: "none"}}
			>
				{currentOrganisation.displayName}
			</Button>

			<Popover
				open={Boolean(selectorAnchor)}
				anchorEl={selectorAnchor}
				slotProps={{
					paper: {
						sx: {
							maxWidth: 280,
							display: "flex",
							flexDirection: "column",
							maxHeight: (theme) => `calc(100vh - ${theme.spacing(2 + 2)})`,
						},
					},
				}}
				disableScrollLock
				onClose={closeSelector}
			>
				<OrganisationSelector
					currentOrganisation={currentOrganisation}
					organisations={organisations}
					showCurrentIfUserNotInOrganisation
					onClose={closeSelector}
				/>

				<Divider />

				<Typography
					variant="caption"
					align="justify"
					component="div"
					sx={{px: 2, py: 1}}
				>
					<Localized id="organisation-selector-deprecation-notice">
						Now you can always access organisation selector from the account
						menu in the top-right corner. Soon it will be removed from the home
						page.
					</Localized>
				</Typography>
			</Popover>
		</>
	);
}

export default NavigationBar;
